import { isInternalRole } from '../../util/roles';
import { getScopeRole } from '@/services/current-context';

// Internal-team predicate for asset-management team tools. DV-2751:
// INTERNAL_POST_PROCESSING sits alongside INTERNAL_ADMIN — that role exists
// precisely to run these tools, and gating them on admin left it with nothing.
// DV-2820: this is now the ONLY dimension separating internal capabilities
// from client-facing ones; the origin gate that used to sit beside it is gone.
//
// NOTE this reads localStorage, which a user can edit. It is a UI-affordance
// gate, not an authorization boundary — see canDeleteAssets() below.
export function isInternalAssetTeamUser(
  role: string | null = getScopeRole()
): boolean {
  return isInternalRole(role);
}

/**
 * The client-facing edit suite: captioning, reorder, hide/unhide, upload,
 * download-hidden, the unsynced note. DV-2820 opened these to ALL users in
 * ALL environments, so this is unconditionally true.
 *
 * Kept as a function rather than inlined at its ~10 call sites for one
 * release: it is the single-line lever to dark the suite again if the rollout
 * goes wrong. FOLLOW-UP: once the rollout has settled, delete this and drop
 * the now-dead conditionals at the call sites — an always-true "gate" is
 * exactly the kind of thing that rots if it outlives its rollback window.
 */
export function canUseEditSuite(): boolean {
  return true;
}

// Upload is part of the client-facing ungate (DV-2820). Named separately from
// canUseEditSuite() so the staging/upload divergence stays legible.
export function canUploadAssets(): boolean {
  return true;
}

// Virtual staging is internal-only — Product-confirmed, and it stays that way
// through the client-facing ungate. DV-2820: no longer origin-gated, so
// internal users get staging in PROD for the first time.
export function canUseVirtualStaging(
  role: string | null = getScopeRole()
): boolean {
  return isInternalAssetTeamUser(role);
}

// Upload on the Virtual Staging tab. Internal-only and deliberately NOT part
// of the client-facing ungate: composed from isInternalAssetTeamUser() rather
// than canUploadAssets() so loosening upload can never drag staged upload
// along.
export function canUploadStagedAssets(
  role: string | null = getScopeRole()
): boolean {
  return isInternalAssetTeamUser(role);
}

// Upload on the Floor Plan tab (DV-3735). Internal-only (Tim, 2026-10-27),
// composed from isInternalAssetTeamUser() for the same reason as
// canUploadStagedAssets(): loosening Photos upload must not loosen this.
export function canUploadFloorPlans(
  role: string | null = getScopeRole()
): boolean {
  return isInternalAssetTeamUser(role);
}

/**
 * Delete/trash — internal-only, and NOT part of the client-facing ungate.
 *
 * ACCEPTED RISK (DV-2820, decision recorded 2026-08-11). This PR dropped the
 * origin gate here, so delete is live in prod for internal roles for the first
 * time. There is NO server-side role check behind it: asset-service has no
 * @PreAuthorize/hasRole anywhere in src/main, and its only server-side guard is
 * assertAssetWithinEntityScope — entity scope, not role (see the DV-2524 note in
 * services/assetApi.ts, which explicitly warns against relaxing this gate on the
 * assumption a backstop exists). So on prod a user who edits localStorage.role
 * to an internal value gets the Trash tab and Delete Selected, and the purge
 * succeeds against photos within their own entity: permanent, unrecoverable
 * deletion that the origin gate previously made unreachable.
 *
 * Raised before merge and accepted — the attack needs deliberate tampering and
 * is confined to the tamperer's own entity. The real fix is a server-side role
 * check on the four trash/delete endpoints; re-tighten here if that lands, and
 * do not widen this gate further meanwhile.
 *
 * Separately, the page requires photoset context (hasPhotoset &&
 * selectedPhotosetId): the trash endpoints resolve entity scope through the
 * photoset, so a photoset-less asset 400s. Backend constraint, not a dial.
 */
export function canDeleteAssets(
  role: string | null = getScopeRole()
): boolean {
  return isInternalAssetTeamUser(role);
}
